import React, { FC, useEffect, useRef, useState } from 'react';
import { gql } from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { z } from 'zod';

import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import { useTheme } from '../../../hooks/useTheme';
import styles from './CustomComponent.module.css';

export const PING_INTERVAL_MS = 300;

export interface MessageProps {
  guestReadyToReceiveMessages?: boolean;
  guestContentLoaded?: boolean;
  guestDocumentSize?: string;
  parentWindowId?: string;
}

export interface CustomComponentProps {
  featureName?: string;
  componentId: string;
  guestComponentSettings?: {
    url?: string | null;
    settings?: Record<string, unknown> | null;
    graphqlQuery?: string | null;
  } | null;
}

const customComponentTrackingEventSchema = z.object({
  eventType: z.union([
    z.literal(NarvarEventTypes.COMPONENT_RENDER),
    z.literal(NarvarEventTypes.CLICK),
  ]),
  clickSubType: z.string().optional(),
});

const CustomComponent: FC<CustomComponentProps> = ({
  featureName,
  componentId,
  guestComponentSettings,
}) => {
  const {
    panelcontainerNoneNone: {
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
    } = {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  } = useTheme();

  const { state } = usePageContext();

  const [guestReadyForMessages, setGuestReady] = useState(false);
  const [guestIsLoaded, setGuestContentLoaded] = useState(false);
  const [iFrameHeight, setIFrameHeight] = useState('0');
  const pingGuestInterval = useRef<number>();

  const logEvent = useAnalyticsPost();

  const { graphqlQuery, url } = guestComponentSettings || {
    graphqlQuery: '',
    url: '',
  };
  const guestAppUrl = url || '';
  const query = graphqlQuery
    ? gql(graphqlQuery)
    : gql('query exampleQuery { exampleProperty }');

  const { data } = useQuery(query, {
    variables: state,
    skip: !graphqlQuery,
  });

  const [iFrameRef, updateIFrameRef] = useState<HTMLIFrameElement | null>(null);

  const baseLogInfo = {
    eventComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
    eventFeature: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
    feature: featureName,
    pageComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
  };

  useEffect(
    () => {
      const receiveMessage = ({
        data: {
          guestReadyToReceiveMessages,
          guestContentLoaded,
          guestDocumentSize,
          parentWindowId,
          trackEvent,
        },
        origin,
      }: MessageEvent): void => {
        if (!guestAppUrl.includes(origin) || parentWindowId !== componentId)
          return;

        if (guestReadyToReceiveMessages) setGuestReady(true);
        if (guestContentLoaded) setGuestContentLoaded(true);
        if (guestDocumentSize) setIFrameHeight(guestDocumentSize as string);
        if (trackEvent !== undefined) {
          try {
            const eventValidated =
              customComponentTrackingEventSchema.parse(trackEvent);
            logEvent({
              ...baseLogInfo,
              eventType: eventValidated.eventType,
              clickSubType: eventValidated.clickSubType,
            });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(
              e instanceof Error
                ? `Invalid tracking event: ${e.message}`
                : 'Invalid tracking event',
            );
          }
        }
      };

      window.addEventListener('message', receiveMessage);

      return (): void => window.removeEventListener('message', receiveMessage);
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [guestAppUrl, componentId],
  );

  useEffect(
    () => {
      if (iFrameRef === null) return undefined;

      const { contentWindow: hostIFrameRef } = iFrameRef;
      if (!hostIFrameRef) return undefined;

      if (!guestReadyForMessages) {
        const ping = (): void => {
          try {
            hostIFrameRef.postMessage(
              {
                type: 'windowId',
                data: {
                  parentWindowId: componentId,
                },
              },
              guestAppUrl,
            );
          } catch (e) {
            if (e instanceof Error) {
              // eslint-disable-next-line no-console
              console.error('Custom Component - error in ping postMessage');
              // eslint-disable-next-line no-console
              console.table({
                errorMessage: e.message,
                featureName,
                componentId,
              });
            } else {
              // eslint-disable-next-line no-console
              console.error('Custom Component - error in ping postMessage');
              // eslint-disable-next-line no-console
              console.table({
                featureName,
                componentId,
              });
            }
          }
        };
        ping();
        pingGuestInterval.current = window.setInterval(ping, PING_INTERVAL_MS);
        return (): void => window.clearInterval(pingGuestInterval.current);
      }
      window.clearInterval(pingGuestInterval.current);

      try {
        hostIFrameRef.postMessage(
          {
            type: 'settings',
            data: {
              pageContext: state,
              settings:
                (guestComponentSettings && guestComponentSettings.settings) ||
                {},
              queryData: data || {},
            },
          },
          guestAppUrl,
        );
      } catch (e) {
        if (e instanceof Error) {
          // eslint-disable-next-line no-console
          console.error('Custom Component - error in settings postMessage');
          // eslint-disable-next-line no-console
          console.table({
            errorMessage: e.message,
            featureName,
            componentId,
          });
        } else {
          // eslint-disable-next-line no-console
          console.error('Custom Component - error in settings postMessage');
          // eslint-disable-next-line no-console
          console.table({
            featureName,
            componentId,
          });
        }
      }
      return undefined;
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      iFrameRef,
      componentId,
      data,
      guestAppUrl,
      guestComponentSettings,
      guestReadyForMessages,
      state,
    ],
  );

  useEffect(
    () => {
      if (!guestIsLoaded) return;
      logEvent({
        ...baseLogInfo,
        eventType: NarvarEventTypes.COMPONENT_RENDER,
      });
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [guestIsLoaded],
  );

  if (
    guestComponentSettings === null ||
    guestComponentSettings === undefined ||
    guestComponentSettings.url === null ||
    guestComponentSettings.url === undefined
  )
    return null;

  return (
    <iframe
      ref={(ref): void => updateIFrameRef(ref)}
      className={styles.iframe}
      // TODO: Set the title of this with a translation
      title="iFrameComponent"
      src={guestAppUrl}
      sandbox="allow-popups allow-scripts allow-same-origin allow-forms"
      style={{
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        display: guestIsLoaded ? 'block' : 'none',
        height: iFrameHeight,
      }}
    />
  );
};

export default CustomComponent;
